import { useEffect } from 'react';

const rawSiteUrl = import.meta.env.VITE_SITE_URL as string | undefined;

export const SITE_URL = (
  rawSiteUrl && rawSiteUrl.trim() ? rawSiteUrl.trim() : 'https://allchinabuyspread.run'
).replace(/\/+$/, '');

export const SITE_NAME = 'AllChinaBuy Spreadsheet';

export const SITE_LOGO_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg';

export interface SeoData {
  title: string;
  description: string;
  keywords?: string;
  robots?: string;
  canonicalPath?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogSiteName?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
}

interface MetaKey {
  attr: 'name' | 'property';
  key: string;
}

const MANAGED_META: MetaKey[] = [
  { attr: 'name', key: 'description' },
  { attr: 'name', key: 'keywords' },
  { attr: 'name', key: 'robots' },
  { attr: 'property', key: 'og:type' },
  { attr: 'property', key: 'og:title' },
  { attr: 'property', key: 'og:description' },
  { attr: 'property', key: 'og:url' },
  { attr: 'property', key: 'og:image' },
  { attr: 'property', key: 'og:site_name' },
  { attr: 'name', key: 'twitter:title' },
  { attr: 'name', key: 'twitter:description' },
  { attr: 'name', key: 'twitter:image' },
];

const touched = new Set<string>();

function selector(attr: string, key: string) {
  return `meta[${attr}="${key}"]`;
}

export function applySeo(seo: SeoData) {
  document.title = seo.title;

  const canonical = seo.canonicalPath ? `${SITE_URL}${seo.canonicalPath}` : '';

  const next = new Map<string, { attr: 'name' | 'property'; key: string; content: string }>();
  const put = (attr: 'name' | 'property', key: string, content?: string) => {
    if (content) next.set(`${attr}:${key}`, { attr, key, content });
  };

  put('name', 'description', seo.description);
  put('name', 'keywords', seo.keywords);
  put('name', 'robots', seo.robots);
  put('property', 'og:type', seo.ogType);
  put('property', 'og:title', seo.ogTitle);
  put('property', 'og:description', seo.ogDescription);
  put('property', 'og:url', canonical);
  put('property', 'og:image', seo.ogImage);
  put('property', 'og:site_name', seo.ogSiteName);
  put('name', 'twitter:title', seo.twitterTitle);
  put('name', 'twitter:description', seo.twitterDescription);
  put('name', 'twitter:image', seo.twitterImage);

  // Remove stale managed tags first so metadata from a previous route cannot leak.
  for (const m of MANAGED_META) {
    const id = `${m.attr}:${m.key}`;
    if (touched.has(id) && !next.has(id)) {
      const el = document.head.querySelector<HTMLMetaElement>(selector(m.attr, m.key));
      if (el) el.remove();
      touched.delete(id);
    }
  }

  next.forEach(({ attr, key, content }) => {
    let el = document.head.querySelector<HTMLMetaElement>(selector(attr, key));
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
    touched.add(`${attr}:${key}`);
  });

  let canonicalEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (canonical) {
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);
  } else if (canonicalEl) {
    canonicalEl.remove();
  }
}

export function useSeo(seo: SeoData) {
  const key = JSON.stringify(seo);
  useEffect(() => {
    applySeo(seo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}